import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { api, type Me } from '../lib/api';
import './Layout.css';

interface Props {
  me: Me;
}

const NAV = [
  { to: '/', label: 'Fleet', end: true },
  { to: '/review', label: 'Review queue' },
  { to: '/audit', label: 'Audit trail' },
  { to: '/billing', label: 'Billing' },
];

export function Layout({ me }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  /*
   * Ends an impersonation and swaps the operator's own console session back in.
   *
   * A hard navigation rather than a router push, for the same reason as starting one: the
   * session cookie has just changed identity, and every cached query in memory belongs to
   * the carrier. Reloading is the only way to be certain none of it is still on screen.
   * If the console session had expired in the meantime the server clears both cookies and
   * this lands on the login form, which is the right failure — better a re-login than a
   * live session left open inside a carrier's workspace.
   */
  const stopImpersonation = useMutation({
    mutationFn: () => api.post('/api/admin/impersonation/stop'),
    onSettled: () => {
      queryClient.clear();
      window.location.href = '/';
    },
  });

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <aside className="sidebar">
        <div className="brand">
          {/* Set in the serif to match milepostsafety.com, where the practice's name lives. */}
          <span className="brand-mark">Milepost</span>
          <span className="brand-sub">Compliance</span>
        </div>

        <nav aria-label="Sections">
          <ul className="nav">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.end} className="nav-link">
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-foot">
          {me.tenant && (
            <div className="tenant">
              <span className="tenant-name">{me.tenant.name}</span>
              {me.tenant.dot_number && (
                <span className="tenant-dot cite">USDOT {me.tenant.dot_number}</span>
              )}
              {me.tenant.status !== 'active' && (
                <span className="tenant-status">{me.tenant.status}</span>
              )}
            </div>
          )}
          <div className="account">
            <span className="account-name">{me.user.fullName}</span>
            <span className="account-role cite">{me.user.role.replace('_', ' ')}</span>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </aside>

      {me.tenant?.status === 'past_due' && (
        <div className="billing-banner" role="status">
          A payment did not go through. Nothing has been deleted — <a href="/billing">update billing</a>{' '}
          to keep making changes.
        </div>
      )}
      {me.tenant?.status === 'suspended' && (
        <div className="billing-banner is-hard" role="status">
          This workspace is read-only because the subscription lapsed. Your records are intact and
          can still be viewed and exported. <a href="/billing">Update billing</a> to make changes again.
        </div>
      )}
      {me.impersonation && (
        <div className="impersonation-banner" role="status">
          You are viewing this workspace as Milepost staff, read-only.
          {me.impersonation.reason ? ` Reason: ${me.impersonation.reason}` : ''}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => stopImpersonation.mutate()}
            disabled={stopImpersonation.isPending}
          >
            {stopImpersonation.isPending ? 'Leaving…' : 'Return to console'}
          </button>
        </div>
      )}

      <main id="main" className="main">
        <Outlet />
      </main>
    </div>
  );
}
